import styled from 'styled-components';

import { DownArrowSmallIcon } from '../../icons';
import { fontSizes, fontWeights, zIndexes } from '../../theme';
import colors from '../../theme/constants/colors';
import { SortOrder } from '../../types';

export const rowHeight = 48;
export const borderSpacing = 4;

export const TableContainer = styled.div`
  position: relative;
  display: flex;
  flex: 1 1 auto;
  padding: 0 16px;
  background-color: ${colors.pearl};
  border-radius: 12px;
`;

interface ScrollContainerProps {
  $isScrollDisabled?: boolean;
}

export const ScrollContainer = styled.div<ScrollContainerProps>`
  flex: 1 1 auto;
  overflow: ${({ $isScrollDisabled }) => !$isScrollDisabled && 'auto'};

  table {
    width: 100%;
    border-spacing: 0 ${borderSpacing}px;
    border-collapse: separate;
  }
`;

interface HeadCellProps {
  'data-is-sortable'?: boolean;
  width?: number | string;
  $isSticky?: boolean;
  $stickyOffset?: number;
}

export const HeadCell = styled.th<HeadCellProps>`
  position: sticky;
  top: 4px;
  left: ${({ $isSticky, $stickyOffset }) => $isSticky && `${$stickyOffset}px`};
  z-index: ${({ $isSticky }) =>
    $isSticky ? zIndexes.tableStickyColumn : zIndexes.tableHeader};
  min-width: ${({ width }) => width && `${width}px`};
  height: 40px;
  padding: 0 8px;
  color: ${colors.pigeon};
  font-weight: ${fontWeights.regular};
  font-size: ${fontSizes.xxs};
  letter-spacing: 0.02em;
  white-space: nowrap;
  text-align: start;
  text-transform: uppercase;
  background-color: ${colors.pearl};
  cursor: ${({ 'data-is-sortable': isSortable }) =>
    isSortable ? 'pointer' : 'default'};
  transition: all 0.2s ease-in-out;

  &:hover {
    color: ${({ 'data-is-sortable': isSortable }) => isSortable && colors.navy};
  }

  &:last-child {
    box-shadow: 6px 0 0 ${colors.pearl};
  }

  > span {
    display: flex;
    align-items: center;
  }
`;

interface BodyRowProps {
  $isStriped?: boolean;
  $bgColor?: string;
}

export const BodyRow = styled.tr<BodyRowProps>`
  height: ${rowHeight}px;
  color: ${colors.night};
  font-size: ${fontSizes.m};
  white-space: nowrap;
  border-radius: 12px;
  box-shadow: 3px 3px 5px ${colors.sand};

  td:first-child {
    border-left: 8px solid
      ${({ $isStriped }) => ($isStriped ? colors.success : 'transparent')};
  }

  ${({ $bgColor }) => $bgColor && `td { background-color: ${$bgColor} }`}
`;

interface BodyCellProps {
  width?: number | string;
  $isOverflowVisible?: boolean;
  $isSticky?: boolean;
  $isLastSticky?: boolean;
  $stickyOffset?: number;
}

export const BodyCell = styled.td<BodyCellProps>`
  position: ${({ $isSticky }) => $isSticky && 'sticky'};
  left: ${({ $isSticky, $stickyOffset }) => $isSticky && `${$stickyOffset}px`};
  width: ${({ width }) => width && `${width}px`};
  padding: 0 8px;
  overflow: ${({ $isOverflowVisible }) => !$isOverflowVisible && 'hidden'};
  text-align: left;
  text-overflow: ellipsis;
  background-color: ${colors.white};

  border-right: ${({ $isLastSticky }) =>
    $isLastSticky && `2px solid ${colors.pearl}`};

  &:first-child {
    padding-left: 16px;
    border-radius: 12px 0 0 12px;
  }

  &:last-child {
    padding-right: 16px;
    border-radius: 0 12px 12px 0;
  }
`;

interface ArrowProps {
  $direction?: SortOrder | null;
  $isSorted?: boolean;
}
export const Arrow = styled(DownArrowSmallIcon).attrs({
  'data-testid': 'sortArrow',
})<ArrowProps>`
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  margin-left: 4px;
  transform: ${({ $direction, $isSorted }) => {
    if ($isSorted) {
      if ($direction === 'asc') return 'rotate(0deg)';
      if ($direction === 'desc') return 'rotate(180deg)';
    }

    return null;
  }};
  transition: all 0.3s ease-in-out;
  pointer-events: none;
  fill: ${({ $isSorted }) => ($isSorted ? colors.navy : colors.pigeon)};
`;

/* Used to hide empty space above table header, caused by border-spacing rule on the table element */
export const ScrollOverflowCover = styled.span`
  position: absolute;
  top: 0;
  width: calc(100% - 32px);
  height: 4px;
  background-color: ${colors.pearl};
  pointer-events: none;
`;
